import { SiteLayout } from "@/components/layout/SiteLayout";
import { SEO } from "@/components/SEO";
import { Link } from "wouter";
import { breadcrumbSchema, webPageSchema } from "@/lib/seo";

export default function AIPolicy() {
  const page = webPageSchema({
    path: "/ai-policy",
    title: "AI Use & Editorial Assistance Policy | Shariq Refai, MD, MBA",
    description:
      "How AI tools are and are not used in producing the books, essays, and educational content on shariqrefai.com. Human editorial oversight always comes first.",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "AI Use Policy", path: "/ai-policy" },
    ],
  });
  return (
    <SiteLayout>
      <SEO
        title="AI Use & Editorial Assistance Policy | Shariq Refai, MD, MBA"
        description="How AI tools are and are not used in producing the books, essays, and educational content on shariqrefai.com. Human editorial oversight always comes first."
        url="/ai-policy"
        schema={[
          page,
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "AI Use Policy", path: "/ai-policy" },
          ]),
        ]}
      />

      <header className="pt-20 pb-12 px-4 md:px-8 bg-muted/30 border-b border-border/50">
        <div className="container mx-auto max-w-3xl">
          <nav
            aria-label="Breadcrumb"
            className="mb-6 text-xs tracking-widest uppercase text-muted-foreground"
          >
            <Link href="/" className="hover:text-accent transition-colors">
              Home
            </Link>
            <span className="mx-2" aria-hidden="true">/</span>
            <span aria-current="page">AI Use Policy</span>
          </nav>
          <h1 className="text-4xl md:text-5xl font-serif text-primary leading-snug">
            AI Use &amp; Editorial Assistance Policy
          </h1>
          <p className="mt-4 text-lg text-muted-foreground font-light max-w-2xl">
            How AI tools fit into, and don't replace, the editorial work on
            this site.
          </p>
        </div>
      </header>

      <article className="py-16 md:py-20 px-4 md:px-8">
        <div className="container mx-auto max-w-3xl prose prose-lg max-w-none prose-headings:font-serif prose-headings:font-normal prose-headings:text-primary prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4 prose-p:font-light prose-p:text-secondary prose-p:leading-relaxed prose-a:text-accent hover:prose-a:text-primary prose-strong:text-primary">
          <p>
            <strong>Last updated: September 23, 2026.</strong>
          </p>

          <p>
            shariqrefai.com is an educational author and media platform. The
            books, essays, perspectives, and frameworks on this site are the
            independent work of Shariq Refai, MD, MBA. This page explains
            where AI tools are used in producing that work and, just as
            importantly, where they are not.
          </p>

          <aside
            role="note"
            aria-label="Network-wide AI use statement"
            className="not-prose my-8 border-l-4 border-accent bg-muted/40 p-6 text-base text-primary font-light leading-relaxed"
          >
            <p className="font-medium text-primary mb-3">
              Summary of AI use on this site
            </p>
            <p>
              This website may use AI-assisted tools to support drafting,
              organization, summarization, grammar review, and editorial
              workflows. AI tools do not independently publish content.
              Medical content undergoes human review and editorial
              oversight before publication.
            </p>
            <p className="mt-3">
              AI does not replace human review, medical judgment,
              diagnosis, or treatment decisions.
            </p>
          </aside>

          <h2>What AI May Be Used For</h2>
          <p>
            AI tools may be used to support the editorial workflow behind
            the scenes, including:
          </p>
          <ul>
            <li>Drafting outlines, headers, and structural revisions for review.</li>
            <li>Summarizing long source material that's then independently verified.</li>
            <li>Organizing notes, references, and reading lists.</li>
            <li>Editing for grammar, clarity, tone, and readability.</li>
            <li>Generating alt text and meta descriptions for review.</li>
            <li>General workflow support such as formatting, link checking, and code maintenance for the site itself.</li>
          </ul>

          <h2>What AI Does Not Do Here</h2>
          <ul>
            <li>AI does not independently publish any content on this website.</li>
            <li>AI is not used to generate clinical claims, mechanism statements, medication content, or research conclusions that are then published as authoritative.</li>
            <li>AI is not used to replace medical judgment, clinical reasoning, or the duty of care owed to any individual patient by a licensed clinician.</li>
            <li>AI does not provide diagnosis, treatment, dosing, or individualized medical recommendations through this site, and content produced with AI assistance never does either.</li>
            <li>AI does not interact with site visitors as a chat or advice surface.</li>
          </ul>

          <h2>Human Review Before Publication</h2>
          <p>
            Every piece of long-form content on shariqrefai.com is reviewed,
            edited, and approved by Dr. Refai before publication. Where AI
            assists with drafting or editing, the final voice, framing,
            clinical accuracy, and editorial judgment remain his
            responsibility. See the{" "}
            <Link href="/editorial-process">Editorial Process</Link> page for
            the broader review pipeline.
          </p>

          <h2>Disclosure of Substantial AI Contribution</h2>
          <p>
            If a piece of content is substantially generated by an AI system
            (for example, an AI-drafted explainer published with only light
            human editing), that contribution will be disclosed on or near
            the piece itself. The default assumption for everything on this
            site is human-authored, AI-assisted at most.
          </p>

          <h2>AI in Books and Long-Form Work</h2>
          <p>
            Books published through shrinkMD Publishing follow the same
            standard: AI may be used in research, structural revision, and
            copyediting, but the ideas, frameworks, clinical examples, and
            arguments are the author's own. No book on this site is
            AI-generated in substance.
          </p>

          <h2>AI and Reader Privacy</h2>
          <p>
            Submissions to the contact form, email, and other reader
            communications are not used to train any AI model. See the{" "}
            <Link href="/privacy">Privacy Policy</Link> for details on what
            is and is not collected on this site.
          </p>

          <h2>AI Training on This Site's Content</h2>
          <p>
            The public pages of shariqrefai.com follow the Shrink Network
            crawler policy. AI and LLM crawlers may read, index, cite, and
            train on them, as long as the attribution to Shariq Refai, MD,
            MBA, stays intact and the content isn't presented as medical
            advice or a diagnosis. The full text of the books and any paid or
            downloadable materials aren't covered. The{" "}
            <a href="/ai.txt">ai.txt</a> file states the terms in machine
            readable form, and the{" "}
            <Link href="/copyright">Copyright</Link> page has the full
            position.
          </p>

          <h2>Updates</h2>
          <p>
            Tooling and norms around AI are moving quickly. This policy will
            be updated as practice changes. Material changes will be marked
            with a revised "Last updated" date at the top of this page.
          </p>

          <h2>Contact</h2>
          <p>
            Questions about how AI was used on a specific piece can be sent
            through the <Link href="/contact">contact page</Link> or to{" "}
            <a href="mailto:editor@shariqrefai.com">editor@shariqrefai.com</a>.
          </p>
        </div>
      </article>
    </SiteLayout>
  );
}
